import { useState, type FormEvent, type ReactNode } from 'react';
import { backup, type InfoZalohy } from '../api/client.js';
import { useApi } from '../api/use-api.js';
import {
  Badge,
  Button,
  Card,
  DataRow,
  Field,
  Notice,
  PageHeader,
  SectionTitle,
} from '../components/ui.js';
import { IconArchive, IconDownload } from '../components/icons.js';
import { useSession } from '../auth/session.js';

/*
 * Zalohy.
 *
 * Dve veci, ktore musia byt na obrazovke povedane nahlas:
 *
 *  - **Bez master kluca je zaloha databazy nepouzitelna.** Preto sa
 *    predvolene prikladá - a preto sa archiv sifruje heslom.
 *  - **Obnova sa prejavi az po restarte.** Prepisat databazu, ktoru ma
 *    proces otvorenu, je cesta k jej poskodeniu, takze sa subory len
 *    odlozia.
 */

function PopisZalohy({ info }: { readonly info: InfoZalohy }): ReactNode {
  return (
    <dl className="rounded-(--radius-control) bg-(--color-surface-sunken) px-4 py-1">
      <DataRow label="Vytvorená">{new Date(info.createdAt).toLocaleString('sk-SK')}</DataRow>
      <DataRow label="Verzia LoxLinku">{info.appVersion}</DataRow>
      <DataRow label="Obsahuje master kľúč">
        <Badge tone={info.containsMasterKey ? 'ok' : 'warn'}>
          {info.containsMasterKey ? 'áno' : 'nie'}
        </Badge>
      </DataRow>
      <DataRow label="Súbory">
        {info.files
          .map((f) => `${f.name} (${Math.max(1, Math.round(f.size / 1024))} kB)`)
          .join(', ')}
      </DataRow>
    </dl>
  );
}

function Vytvorenie(): ReactNode {
  const [heslo, setHeslo] = useState('');
  const [znova, setZnova] = useState('');
  const [sKlucom, setSKlucom] = useState(true);
  const [chyba, setChyba] = useState<string | undefined>(undefined);
  const [prebieha, setPrebieha] = useState(false);

  const odosli = (event: FormEvent) => {
    event.preventDefault();
    setChyba(undefined);
    if (heslo !== znova) {
      setChyba('Heslá sa nezhodujú.');
      return;
    }
    setPrebieha(true);
    void backup
      .create(heslo, sKlucom)
      .then(({ blob, fileName }) => {
        const url = URL.createObjectURL(blob);
        const odkaz = document.createElement('a');
        odkaz.href = url;
        odkaz.download = fileName;
        odkaz.click();
        URL.revokeObjectURL(url);
        setHeslo('');
        setZnova('');
      })
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Zálohu sa nepodarilo vytvoriť.');
      })
      .finally(() => setPrebieha(false));
  };

  return (
    <Card className="flex flex-col gap-4 p-4 sm:p-5">
      <SectionTitle>Vytvoriť zálohu</SectionTitle>
      <p className="text-sm text-(--color-text-muted)">
        Záloha obsahuje databázu a — ak to necháš zapnuté — aj master kľúč. Bez neho sa uložené
        heslá a lokálne kľúče nedajú obnoviť, ale zároveň platí, že komu sa dostane súbor aj heslo,
        má prístup ku všetkému. Preto je celý archív šifrovaný.
      </p>

      <form onSubmit={odosli} className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            label="Heslo k zálohe"
            type="password"
            value={heslo}
            onChange={setHeslo}
            required
            autoComplete="new-password"
            hint="Aspoň 12 znakov. Zabudnuté heslo znamená nepoužiteľnú zálohu."
          />
          <Field
            label="Heslo znova"
            type="password"
            value={znova}
            onChange={setZnova}
            required
            autoComplete="new-password"
          />
        </div>

        <label className="flex items-start gap-2.5 text-sm text-(--color-text)">
          <input
            type="checkbox"
            checked={sKlucom}
            onChange={(e) => setSKlucom(e.target.checked)}
            className="mt-0.5 size-4"
          />
          <span>
            Priložiť master kľúč
            <span className="block text-xs text-(--color-text-subtle)">
              Bez neho je záloha použiteľná len na tej istej inštalácii, kde kľúč zostal.
            </span>
          </span>
        </label>

        {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}

        <div>
          <Button
            type="submit"
            variant="primary"
            disabled={prebieha}
            icon={<IconDownload className="size-4" />}
          >
            {prebieha ? 'Vytváram…' : 'Vytvoriť a stiahnuť'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function Obnova({ poZmene }: { readonly poZmene: () => void }): ReactNode {
  const [subor, setSubor] = useState<File | undefined>(undefined);
  const [heslo, setHeslo] = useState('');
  const [info, setInfo] = useState<InfoZalohy | undefined>(undefined);
  const [odlozene, setOdlozene] = useState(false);
  const [chyba, setChyba] = useState<string | undefined>(undefined);
  const [prebieha, setPrebieha] = useState(false);

  const posli = (confirm: boolean) => {
    if (!subor) {
      setChyba('Vyber súbor zálohy.');
      return;
    }
    setChyba(undefined);
    setPrebieha(true);
    void subor
      .arrayBuffer()
      .then((buf) => backup.restore(buf, heslo, confirm))
      .then((odpoved) => {
        setInfo(odpoved.backup);
        setOdlozene(odpoved.staged);
        if (odpoved.staged) poZmene();
      })
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Zálohu sa nepodarilo otvoriť.');
      })
      .finally(() => setPrebieha(false));
  };

  return (
    <Card className="flex flex-col gap-4 p-4 sm:p-5">
      <SectionTitle>Obnoviť zo zálohy</SectionTitle>
      <p className="text-sm text-(--color-text-muted)">
        Najprv sa záloha len otvorí a ukáže, čo v nej je. Obnova sa potvrdzuje zvlášť a prejaví sa
        až po reštarte brány.
      </p>

      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-1.5 text-sm font-medium text-(--color-text)">
          Súbor zálohy
          <input
            type="file"
            accept=".loxbak"
            onChange={(e) => {
              setSubor(e.target.files?.[0]);
              setInfo(undefined);
              setOdlozene(false);
            }}
            className="text-sm text-(--color-text-muted) file:mr-3 file:rounded-(--radius-control) file:border file:border-(--color-border-strong) file:bg-(--color-surface) file:px-3 file:py-1.5 file:text-sm file:text-(--color-text)"
          />
        </label>

        <Field
          label="Heslo k zálohe"
          type="password"
          value={heslo}
          onChange={setHeslo}
          autoComplete="off"
        />

        {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}
        {info && <PopisZalohy info={info} />}

        {odlozene ? (
          <Notice tone="warn">
            <p className="font-medium">Obnova je pripravená.</p>
            <p className="mt-1">
              Prejaví sa po reštarte brány. Pôvodné súbory zostanú vedľa s príponou
              <code> .pred-obnovou</code>, takže sa dá vrátiť späť.
            </p>
          </Notice>
        ) : (
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => posli(false)} disabled={prebieha || !subor}>
              {prebieha ? 'Otváram…' : 'Otvoriť zálohu'}
            </Button>
            {info && (
              <Button variant="danger" onClick={() => posli(true)} disabled={prebieha}>
                Obnoviť z tejto zálohy
              </Button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

export function Backups(): ReactNode {
  const { user } = useSession();
  const stav = useApi((signal) => backup.status(signal));
  const caka = stav.data?.pendingRestore === true;

  if (user?.role !== 'admin') {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title="Zálohy" detail="Databáza, master kľúč a nastavenia." />
        <Notice tone="warn">Zálohy môže vytvárať a obnovovať len správca.</Notice>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Zálohy" detail="Databáza aj master kľúč v jednom zašifrovanom súbore." />

      {caka && (
        <Notice tone="warn">
          <p className="font-medium">Na reštart čaká pripravená obnova.</p>
          <p className="mt-1">
            Po reštarte sa databáza nahradí zálohou.{' '}
            <button
              type="button"
              className="underline"
              onClick={() => {
                void backup.cancelRestore().then(stav.reload);
              }}
            >
              Zrušiť obnovu
            </button>
          </p>
        </Notice>
      )}

      <Vytvorenie />
      <Obnova poZmene={stav.reload} />

      <p className="inline-flex items-start gap-2 text-xs text-(--color-text-subtle)">
        <IconArchive className="size-4 shrink-0" />
        <span>
          Zálohu odlož mimo tento stroj. Keď zhorí disk aj so zálohou, nepomôže ani jedno.
        </span>
      </p>
    </div>
  );
}
